import React, { useState, useEffect, useCallback } from 'react';
import './HomePage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileAlt, faClock, faTriangleExclamation, faCircleCheck } from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import { apiFetch } from '../services/api';
import { useOri } from '../context/OriContext';
import { Link, useNavigate } from 'react-router-dom';
import {
    Grid, Card, CardContent, Typography, Box, Select, MenuItem, FormControl, InputLabel,
    Paper, Skeleton, Alert, Button, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, List, ListItemButton, ListItemText, FormControlLabel, Switch, Stack
} from '@mui/material';
import SrfIcons from './SrfIcons';
import DailyActivityChart, { DailyActivityPoint } from './DailyActivityChart';
import type { Submission } from './SubmissionsList';

interface StatsData {
    submissions: number;
    pending: number;
    complete: number;
    hits: number;
    oldestPendingCreated: string | null;
    series: DailyActivityPoint[];
    recentHits: Submission[];
}

/** Resolves the selected period into a [start, end] window. */
const periodRange = (period: string): { start: Date; end: Date } => {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    switch (period) {
        case 'today':
            start.setHours(0, 0, 0, 0);
            break;
        case 'week':
            start.setDate(now.getDate() - 7);
            break;
        case '30days':
            start.setDate(now.getDate() - 30);
            break;
        case 'month':
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
        case 'lastMonth':
            start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            end = new Date(now.getFullYear(), now.getMonth(), 0);
            break;
        case 'ytd':
            start = new Date(now.getFullYear(), 0, 1);
            break;
        case 'lastYear':
            start = new Date(now.getFullYear() - 1, 0, 1);
            end = new Date(now.getFullYear() - 1, 11, 31);
            break;
    }
    return { start, end };
};

/** Compact "3h 12m" / "2d 4h" age string for a past ISO timestamp. */
const ageOf = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return '0m';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};

const fetchStats = async (ori: string, start: Date, end: Date, includeAuthorized: boolean): Promise<StatsData> => {
    let path = `/api/risc/report/stats?ori=${ori}&start=${start.toISOString()}&end=${end.toISOString()}`;
    if (!includeAuthorized) path += '&includeAuthorized=false';
    const response = await apiFetch(path);
    if (!response.ok) throw new Error('Failed to fetch statistics.');
    return response.json();
};

/** Small "+12 vs prior period" line under a stat value. */
const Delta: React.FC<{ current: number; previous?: number }> = ({ current, previous }) => {
    if (previous === undefined) return null;
    const diff = current - previous;
    const arrow = diff > 0 ? '▲' : diff < 0 ? '▼' : '▬';
    const text = diff === 0 ? 'no change' : `${diff > 0 ? '+' : ''}${diff}`;
    return (
        <Typography variant="caption" color="text.secondary" component="div" className="stat-delta">
            {arrow} {text} vs prior period
        </Typography>
    );
};

const StatTileSkeleton: React.FC = () => (
    <Card className="stat-tile">
        <CardContent className="stat-card-content">
            <Skeleton variant="circular" width={36} height={36} />
            <Box flexGrow={1}>
                <Skeleton width={64} height={34} />
                <Skeleton width={120} height={14} />
            </Box>
        </CardContent>
    </Card>
);

const HomePage: React.FC = () => {
    const firstName = sessionStorage.getItem('firstName') || 'User';
    const [stats, setStats] = useState<StatsData | null>(null);
    const [prevStats, setPrevStats] = useState<StatsData | null>(null);
    const [monthStats, setMonthStats] = useState<StatsData | null>(null);
    const [recentSubs, setRecentSubs] = useState<Submission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { selectedOri, availableOris } = useOri();
    const [period, setPeriod] = useState('today');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [includeAuthorized, setIncludeAuthorized] = useState(true);
    const navigate = useNavigate();

    const agency = availableOris.find(a => a.ori === selectedOri);

    // Same rule as the submissions list: the merged authorized-agency view
    // applies whenever the viewed agency is authorized for others (e.g. a
    // parent seeing its children); the server limits it to accessible ones.
    const canToggleAuthorized = ((agency?.authorized_oris?.length) ?? 0) > 0;

    const openDetails = (submission: Partial<Submission> & { tcn: string }) => {
        navigate(`/submissions/${submission.tcn}`, {
            state: { from: 'dashboard', ori: submission.ori || selectedOri, submission }
        });
    };

    const fetchAll = useCallback(async (silent = false) => {
        if (!selectedOri) return;
        if (!silent) setIsLoading(true);
        setError(null);

        const { start, end } = periodRange(period);
        const duration = end.getTime() - start.getTime();
        const prevEnd = new Date(start.getTime());
        const prevStart = new Date(start.getTime() - Math.max(duration, 1));
        const monthStart = new Date();
        monthStart.setDate(monthStart.getDate() - 30);
        monthStart.setHours(0, 0, 0, 0);

        const results = await Promise.allSettled([
            fetchStats(selectedOri, start, end, includeAuthorized),
            fetchStats(selectedOri, prevStart, prevEnd, includeAuthorized),
            fetchStats(selectedOri, monthStart, new Date(), includeAuthorized),
            apiFetch(`/api/risc/sub?limit=6&ori=${selectedOri}${includeAuthorized ? '' : '&includeAuthorized=false'}`).then(async r => {
                if (!r.ok) throw new Error('Failed to fetch submissions.');
                return (await r.json()).submissions as Submission[];
            }),
        ]);

        const [statsRes, prevRes, monthRes, subsRes] = results;

        if (statsRes.status === 'fulfilled') {
            setStats(statsRes.value as StatsData);
        } else if (!silent) {
            setError('Unable to load statistics. Check your connection and try again.');
        }
        setPrevStats(prevRes.status === 'fulfilled' ? (prevRes.value as StatsData) : null);
        setMonthStats(monthRes.status === 'fulfilled' ? (monthRes.value as StatsData) : null);
        setRecentSubs(subsRes.status === 'fulfilled' ? (subsRes.value as Submission[]) || [] : []);
        if (!silent) setIsLoading(false);
    }, [selectedOri, period, includeAuthorized]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Background auto-refresh (silent - no skeletons/spinners).
    useEffect(() => {
        if (!autoRefresh) return;
        const intervalId = setInterval(() => fetchAll(true), 30000);
        return () => clearInterval(intervalId);
    }, [autoRefresh, fetchAll]);

    const hitRate = stats && stats.submissions > 0 ? Math.round((stats.hits / stats.submissions) * 100) : 0;
    const responseRate = stats && stats.submissions > 0 ? Math.round((stats.complete / stats.submissions) * 100) : 0;
    const oldestPendingAge = stats?.oldestPendingCreated ? ageOf(stats.oldestPendingCreated) : null;
    const pendingIsStale = stats?.oldestPendingCreated
        ? Date.now() - new Date(stats.oldestPendingCreated).getTime() > 30 * 60 * 1000
        : false;

    return (
        <Box sx={{ flexGrow: 1, px: 2, py: 1 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Typography variant="h4" component="h1">Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {agency ? <>{agency.name} · <span className="mono-id">{agency.ori}</span></> : `Welcome back, ${firstName}`}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box display={'flex'} justifyContent={'flex-end'} alignItems="center" gap={1} className="period-box">
                        {canToggleAuthorized && (
                            <FormControlLabel
                                control={<Switch size="small" checked={includeAuthorized} onChange={(e) => setIncludeAuthorized(e.target.checked)} />}
                                label={<Typography variant="body2">Authorized agencies</Typography>}
                                title="Include submissions from agencies your agency is authorized for"
                            />
                        )}
                        <FormControlLabel
                            control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                            label={<Typography variant="body2">Auto-refresh</Typography>}
                        />
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="period-select-label">Period</InputLabel>
                            <Select
                                labelId="period-select-label"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                label="Period"
                            >
                                <MenuItem value="today">Today</MenuItem>
                                <MenuItem value="week">Last 7 Days</MenuItem>
                                <MenuItem value="30days">Last 30 Days</MenuItem>
                                <MenuItem value="month">This Month</MenuItem>
                                <MenuItem value="lastMonth">Last Month</MenuItem>
                                <MenuItem value="ytd">Year to Date</MenuItem>
                                <MenuItem value="lastYear">Last Year</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
            </Grid>

            {error && (
                <Alert
                    severity="error"
                    sx={{ my: 1 }}
                    action={<Button color="inherit" size="small" onClick={() => fetchAll()}>Retry</Button>}
                >
                    {error}
                </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 0 }}>
                {/* Stat tiles */}
                <Grid item xs={12} sm={6} lg={3}>
                    {isLoading ? <StatTileSkeleton /> : (
                        <Card className="stat-tile stat-tile--total">
                            <CardContent className="stat-card-content">
                                <FontAwesomeIcon icon={faFileAlt} className="stat-icon" swapOpacity />
                                <Box>
                                    <Typography component="div" className="stat-value">{stats?.submissions ?? 0}</Typography>
                                    <Typography variant="body2" color="text.secondary" className="stat-label">Submissions</Typography>
                                    <Delta current={stats?.submissions ?? 0} previous={prevStats?.submissions} />
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    {isLoading ? <StatTileSkeleton /> : (
                        <Card className="stat-tile stat-tile--complete">
                            <CardContent className="stat-card-content">
                                <FontAwesomeIcon icon={faCircleCheck} className="stat-icon stat-icon--complete" swapOpacity />
                                <Box>
                                    <Typography component="div" className="stat-value">{stats?.complete ?? 0}</Typography>
                                    <Typography variant="body2" color="text.secondary" className="stat-label">Completed</Typography>
                                    <Typography variant="caption" color="text.secondary" component="div" className="stat-delta">
                                        {responseRate}% response rate
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    {isLoading ? <StatTileSkeleton /> : (
                        <Card className="stat-tile stat-tile--pending">
                            <CardContent className="stat-card-content">
                                <FontAwesomeIcon icon={faClock} className="stat-icon stat-icon--pending" swapOpacity />
                                <Box>
                                    <Typography component="div" className="stat-value">{stats?.pending ?? 0}</Typography>
                                    <Typography variant="body2" color="text.secondary" className="stat-label">Pending</Typography>
                                    {oldestPendingAge && (stats?.pending ?? 0) > 0 && (
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            className="stat-delta"
                                            sx={{ color: pendingIsStale ? 'var(--status-pending)' : 'text.secondary', fontWeight: pendingIsStale ? 600 : 400 }}
                                        >
                                            oldest waiting {oldestPendingAge}
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    {isLoading ? <StatTileSkeleton /> : (
                        <Card className="stat-tile stat-tile--hits">
                            <CardContent className="stat-card-content">
                                <FontAwesomeIcon icon={faTriangleExclamation} className="stat-icon positive-hit" swapOpacity />
                                <Box>
                                    <Typography component="div" className="stat-value">{stats?.hits ?? 0}</Typography>
                                    <Typography variant="body2" color="text.secondary" className="stat-label">Hits</Typography>
                                    <Typography variant="caption" color="text.secondary" component="div" className="stat-delta">
                                        {hitRate}% hit rate
                                        {prevStats !== null && ` · ${(stats?.hits ?? 0) - prevStats.hits >= 0 ? '+' : ''}${(stats?.hits ?? 0) - prevStats.hits} vs prior`}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                {/* Left column: activity chart over recent submissions */}
                <Grid item xs={12} lg={8}>
                    <Stack spacing={2}>
                        <Paper sx={{ p: 1.5 }}>
                            <Typography variant="h6" gutterBottom>Activity — Last 30 Days</Typography>
                            {isLoading
                                ? <Skeleton variant="rectangular" height={130} />
                                : <DailyActivityChart series={monthStats?.series ?? []} days={30} />}
                        </Paper>

                        <Paper sx={{ p: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Typography variant="h6">Recent Submissions</Typography>
                                <Button component={Link} to="/submissions" size="small">View all</Button>
                            </Box>
                            {isLoading ? (
                                <Skeleton variant="rectangular" height={200} />
                            ) : recentSubs.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                    No submissions yet. Submissions from your devices appear here as they arrive.
                                </Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>TCN</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell className="hide-on-mobile">Agency</TableCell>
                                                <TableCell>Match</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell className="hide-on-mobile">Submitted</TableCell>
                                                <TableCell className="hide-on-mobile">By</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recentSubs.map(sub => (
                                                <TableRow key={sub.tcn} hover onClick={() => openDetails(sub)} sx={{ cursor: 'pointer' }}>
                                                    <TableCell className="mono-id">{sub.tcn}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{sub.name || ''}</TableCell>
                                                    <TableCell className="hide-on-mobile mono-id">{sub.ori}</TableCell>
                                                    <TableCell>
                                                        <SrfIcons fbiSrf={sub.fbiSrf} stateSrf={sub.stateSrf} stateCaseType={sub.stateCaseType} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={`status ${sub.state === 'COMPLETE' ? 'status-complete' : 'status-pending'}`}>
                                                            {sub.state === 'COMPLETE' ? 'Complete' : 'Pending'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="hide-on-mobile">{ageOf(sub.created)} ago</TableCell>
                                                    <TableCell className="hide-on-mobile">{sub.submittedBy}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Stack>
                </Grid>

                {/* Right column: recent hits, stretched to match the left column */}
                <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
                    <Paper sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Typography variant="h6" gutterBottom>Recent Hits — 30 Days</Typography>
                        {isLoading ? (
                            <Skeleton variant="rectangular" height={220} />
                        ) : !monthStats || monthStats.recentHits.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                No hits in the last 30 days.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
                                <List dense disablePadding>
                                    {monthStats.recentHits.map(hit => (
                                        <ListItemButton key={hit.tcn} disableGutters divider onClick={() => openDetails(hit)} sx={{ pr: 8, position: 'relative' }}>
                                            <ListItemText
                                                primary={hit.name
                                                    ? <span style={{ fontWeight: 600 }}>{hit.name}</span>
                                                    : <span className="mono-id">{hit.tcn}</span>}
                                                secondary={`${hit.name ? `${hit.tcn} · ` : ''}${ageOf(hit.created)} ago${hit.stateCaseType ? ` · ${hit.stateCaseType.toLowerCase()}` : ''}${hit.stateOca ? ` · OCA ${hit.stateOca}` : ''}`}
                                            />
                                            <Box sx={{ position: 'absolute', right: 8 }}>
                                                <SrfIcons fbiSrf={hit.fbiSrf} stateSrf={hit.stateSrf} stateCaseType={hit.stateCaseType} />
                                            </Box>
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HomePage;
